import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnInit, Signal, signal, viewChild } from '@angular/core';

import { DATE_FILTERS } from '@app/shared/contants/variables';
import { DashboardService } from '@app/shared/services/dashboard.service';
import { IFiltredDashboardResponse } from '@app/shared/interfaces/dashboard.interface';
import { BasicChartComponent } from '@app/shared/components/basic-chart/basic-chart.component';
import { BestSellerRouteComponent } from './tables/best-seller-route/best-seller-route.component';
import { AgentInformationComponent } from './tables/agent-information/agent-information.component';
import { AirlineStatisticsComponent } from './tables/airline-statistics/airline-statistics.component';
import { DoughnutChartComponent } from '@app/shared/components/doughnut-chart/doughnut-chart.component';
import { StackedBarChartComponent } from '@app/shared/components/stacked-bar-chart/stacked-bar-chart.component';
import { AgentReservationCancelChangeComponent } from './tables/agent-reservation-cancel-change/agent-reservation-cancel-change.component';

import { Checkbox } from 'primeng/checkbox';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { Router } from '@angular/router';
import { PrimeTemplate } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import dayjs from 'dayjs';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';
import { CardComponent } from '@app/shared/components/card/card.component';

@Component({
    selector: 'app-dashboard',
    standalone: true,
    templateUrl: 'dashboard.component.html',
    styleUrls: ['dashboard.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        DatePicker,
        Checkbox,
        AgentInformationComponent,
        PrimeTemplate,
        DoughnutChartComponent,
        StackedBarChartComponent,
        BasicChartComponent,
        BestSellerRouteComponent,
        AgentReservationCancelChangeComponent,
        AirlineStatisticsComponent,
        ButtonModule,
        Menu,
        PageHeaderComponent,
        CardComponent
    ]
})
export class DashboardComponent implements OnInit {
    mainDatePicker = viewChild<DatePicker>('mainDatePicker');
    dashboardService = inject(DashboardService);

    rangeDates = signal<Date[] | undefined>(undefined);
    selectedDateRange = signal<string>('this_year');
    flightDateBased = signal<boolean>(false);
    loading = signal<boolean>(false);
    dashboardAllData = signal<IFiltredDashboardResponse | undefined>(undefined);
    salesChannelData = signal<IFiltredDashboardResponse['salesChannel'] | undefined>(undefined);
    bestSellerRouteData = signal<IFiltredDashboardResponse['bestSellerRoute'] | undefined>(undefined);
    salesInformationData = signal<IFiltredDashboardResponse['salesInformation'] | undefined>(undefined);
    agentInformationData = signal<IFiltredDashboardResponse['agentInformation'] | undefined>(undefined);
    airlineStatisticsData = signal<IFiltredDashboardResponse['airlineStatistic'] | undefined>(undefined);
    agentBiStatisticsData = signal<IFiltredDashboardResponse['agentBiStatistic'] | undefined>(undefined);
    providerInformationData = signal<IFiltredDashboardResponse['providerInformation'] | undefined>(undefined);
    seatSaleInformationValue = signal<number>(0);
    totalReservationValue = signal<number>(0);

    reportButtonItems: MenuItem[] = [];
    dateRanges = [
        { label: 'Today', value: 'today' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: 'This week', value: 'this_week' },
        { label: 'Last week', value: 'last_week' },
        { label: 'This month', value: 'this_month' },
        { label: 'Last month', value: 'last_month' },
        { label: 'This year', value: 'this_year' },
        { label: 'Last year', value: 'last_year' }
    ];

    constructor(private router: Router) {
        effect(() => {
            this.onChangeFilterDate();
        });

        effect(() => {
            this.onChangeFlightBased();
        });
    }

    ngOnInit(): void {
        this.defineFilter();
        this.defineReportButtonItems();

        this.getDashboardData();
    }

    defineFilter() {
        const beginDate = sessionStorage.getItem(DATE_FILTERS.BEGIN_DATE);
        const endDate = sessionStorage.getItem(DATE_FILTERS.END_DATE);
        const flightDateBased = sessionStorage.getItem(DATE_FILTERS.FLIGHT_DATE_SEARCH) === 'true';
        const savedRange = sessionStorage.getItem(DATE_FILTERS.SELECTED_RANGE);

        if (beginDate && endDate && savedRange) {
            this.rangeDates.set([new Date(beginDate), new Date(endDate)]);
            this.selectedDateRange.set(savedRange);
        } else {
            // Default: This year
            this.setDateRange('this_year');
        }

        this.flightDateBased.set(flightDateBased);
    }

    defineReportButtonItems() {
        this.reportButtonItems = [
            { label: 'Sales Report', routerLink: '/reports/sales-report' },
            {
                label: 'OTA Report',
                routerLink: '/reports/ota-report',
                disabled: this.flightDateBased()
            },
            {
                label: 'Price Mismatch Report',
                routerLink: '/reports/price-mismatch',
                disabled: this.flightDateBased()
            },
            {
                label: 'Sale Performance',
                routerLink: '/reports/sale-performance',
                disabled: this.flightDateBased()
            }
        ];
    }

    getDashboardData() {
        this.loading.set(true);
        this.totalReservationValue.set(0);

        this.dashboardService
            .getFiltredDashboard(
                this.flightDateBased().toString(),
                dayjs(this.rangeDates()?.[0]).format('YYYY-MM-DD'),
                dayjs(this.rangeDates()?.[1]).format('YYYY-MM-DD')
            )
            .subscribe({
                next: (response) => {
                    this.dashboardAllData.set(response);
                    this.salesChannelData.set(response.salesChannel);
                    this.bestSellerRouteData.set(response.bestSellerRoute);
                    this.salesInformationData.set(response.salesInformation);
                    this.agentInformationData.set(response.agentInformation);
                    this.airlineStatisticsData.set(response.airlineStatistic);
                    this.agentBiStatisticsData.set(response.agentBiStatistic);
                    this.providerInformationData.set(response.providerInformation);

                    this.seatSaleInformationValue.set(
                        response.salesInformation.adultSeat + response.salesInformation.childSeat + response.salesInformation.infantSeat
                    );

                    response.agentInformation.results.map((item) => {
                        this.totalReservationValue.update((value) => value + item.reservation);
                    });

                    this.loading.set(false);
                },
                error: (error) => {
                    console.log(error);
                    this.loading.set(false);
                }
            });
    }

    // Date Picker Filter Operations
    setDateRange(range: string) {
        this.selectedDateRange.set(range);
        const today = new Date();

        switch (range) {
            case 'today':
                this.rangeDates.set([today, today]);
                break;
            case 'yesterday':
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                this.rangeDates.set([yesterday, yesterday]);
                break;
            case 'this_week':
                const thisWeekStart = new Date(today);
                thisWeekStart.setDate(today.getDate() - today.getDay());
                this.rangeDates.set([thisWeekStart, today]);
                break;
            case 'last_week':
                const lastWeekStart = new Date(today);
                lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
                const lastWeekEnd = new Date(today);
                lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
                this.rangeDates.set([lastWeekStart, lastWeekEnd]);
                break;
            case 'this_month':
                const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
                this.rangeDates.set([thisMonthStart, today]);
                break;
            case 'last_month':
                const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                this.rangeDates.set([lastMonthStart, lastMonthEnd]);
                break;
            case 'this_year':
                const thisYearStart = new Date(today.getFullYear(), 0, 1);
                this.rangeDates.set([thisYearStart, today]);
                break;
            case 'last_year':
                const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
                const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
                this.rangeDates.set([lastYearStart, lastYearEnd]);
                break;
        }
    }

    cancel() {
        this.rangeDates.set(undefined);
        this.mainDatePicker()?.toggle();
    }

    apply() {
        this.mainDatePicker()?.toggle();
        this.getDashboardData();
    }

    onChangeFilterDate() {
        sessionStorage.setItem(DATE_FILTERS.BEGIN_DATE, dayjs(this.rangeDates()?.[0]).format('YYYY-MM-DD'));
        sessionStorage.setItem(DATE_FILTERS.END_DATE, dayjs(this.rangeDates()?.[1]).format('YYYY-MM-DD'));
        sessionStorage.setItem(DATE_FILTERS.SELECTED_RANGE, this.selectedDateRange());
    }

    onChangeFlightBased() {
        sessionStorage.setItem(DATE_FILTERS.FLIGHT_DATE_SEARCH, this.flightDateBased().toString());
    }
}
